import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Wand2, Upload, Image, Link2, Loader2, Eye, Check, Copy, RotateCcw,
  ArrowLeft, Sparkles, Layout, FileText, Target, Palette, Users, X,
  ChevronDown, Play, Save, Layers
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const GlassCard = ({ children, className = '', ...props }) => (
  <div className={`bg-[#171A2C]/60 backdrop-blur-xl border border-white/5 shadow-[0_8px_32px_0_rgba(0,0,0,0.36)] rounded-2xl ${className}`} {...props}>{children}</div>
);

const blockTypeLabels = {
  hero: 'Hero', banner: 'Banner', products: 'Products', about: 'About',
  gallery: 'Gallery', video: 'Video', testimonial: 'Testimonial', signup: 'Signup',
  booking: 'Booking', donations: 'Donations', form: 'Form', embed: 'Embed',
  single_image: 'Image', custom_text: 'Text',
};

const blockTypeColors = {
  hero: '#8B5CF6', banner: '#3B82F6', products: '#E63946', about: '#00CC99',
  gallery: '#F59E0B', video: '#EC4899', testimonial: '#10B981', signup: '#6366F1',
  booking: '#D4A574', donations: '#EF4444', form: '#8B5CF6', embed: '#06B6D4',
  single_image: '#F97316', custom_text: '#94A3B8',
};

export default function MasterAIRedesign() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(siteId || '');
  const [prompt, setPrompt] = useState('');
  const [referenceUrls, setReferenceUrls] = useState(['']);
  const [referenceImages, setReferenceImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [applying, setApplying] = useState(false);
  const [history, setHistory] = useState([]);
  const [style, setStyle] = useState({
    business_type: '', color_palette: '', target_audience: '',
    layout_style: 'modern', goals: '', sections_wanted: [],
  });
  const [targetPage, setTargetPage] = useState('homepage');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const fileRef = useRef(null);

  const token = localStorage.getItem('masterToken');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/master/sites`, { headers }).then(res => {
      setSites(res.data || []);
      if (!selectedSite && res.data?.length > 0) setSelectedSite(res.data[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedSite) {
      axios.get(`${API}/master/sites/${selectedSite}/ai-redesigns`, { headers })
        .then(res => setHistory(res.data || []))
        .catch(() => {});
    }
  }, [selectedSite]);

  const handleImageUpload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const newImages = [];
    for (const file of Array.from(files)) {
      const fd = new FormData();
      fd.append('file', file);
      try {
        const res = await axios.post(`${API}/media/upload?category=ai-reference`, fd, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' }
        });
        newImages.push(process.env.REACT_APP_BACKEND_URL + res.data.url);
      } catch { toast.error(`Failed to upload ${file.name}`); }
    }
    setReferenceImages(prev => [...prev, ...newImages]);
    setUploading(false);
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) { toast.error('Enter a design prompt'); return; }
    if (!selectedSite) { toast.error('Select a site first'); return; }
    setGenerating(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/master/sites/${selectedSite}/ai-redesign-full`, {
        prompt,
        reference_urls: referenceUrls.filter(u => u.trim()),
        reference_images: referenceImages,
        target_page: targetPage,
        style_preferences: style,
      }, { headers });
      setResult(res.data);
      setHistory(prev => [res.data.suggestion, ...prev]);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally { setGenerating(false); }
  };

  const handleApply = async () => {
    if (!result?.blocks) return;
    if (!window.confirm(`Apply this AI design to ${targetPage}? This replaces the current content.`)) return;
    setApplying(true);
    try {
      await axios.post(`${API}/master/sites/${selectedSite}/ai-redesign-apply`, {
        blocks: result.blocks,
        target_page: targetPage,
      }, { headers });
      toast.success('Design applied successfully!');
    } catch { toast.error('Failed to apply design'); }
    finally { setApplying(false); }
  };

  const handleApplyFromHistory = async (redesign) => {
    if (!window.confirm('Apply this saved design?')) return;
    setApplying(true);
    try {
      await axios.post(`${API}/master/sites/${selectedSite}/ai-redesign-apply`, {
        blocks: redesign.blocks,
        target_page: redesign.target_page || 'homepage',
      }, { headers });
      toast.success('Design applied!');
    } catch { toast.error('Failed to apply'); }
    finally { setApplying(false); }
  };

  const sectionOptions = ['Hero', 'Products', 'About', 'Gallery', 'Testimonial', 'Video', 'Booking', 'Signup', 'Banner', 'Embed', 'Form', 'Donations'];

  return (
    <div className="space-y-6" data-testid="ai-redesign-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-2xl bg-gradient-to-br from-[#8B5CF6] to-[#EC4899] shadow-[0_0_20px_rgba(139,92,246,0.4)]">
            <Wand2 size={22} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">AI Redesign</h1>
            <p className="text-sm text-slate-400">Generate or redesign website pages with AI</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Prompt + Controls */}
        <div className="lg:col-span-2 space-y-4">
          {/* Site + Page Selector */}
          <GlassCard className="p-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-400 text-xs mb-1.5 block">Target Site</Label>
                <select value={selectedSite} onChange={(e) => setSelectedSite(e.target.value)} className="w-full bg-[#1E1E3A] border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm" data-testid="site-selector">
                  {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-slate-400 text-xs mb-1.5 block">Target Page</Label>
                <select value={targetPage} onChange={(e) => setTargetPage(e.target.value)} className="w-full bg-[#1E1E3A] border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm" data-testid="page-selector">
                  <option value="homepage">Homepage</option>
                  <option value="about">About</option>
                  <option value="contact">Contact</option>
                  <option value="services">Services</option>
                </select>
              </div>
            </div>
          </GlassCard>

          {/* Prompt Area */}
          <GlassCard className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={16} className="text-[#EC4899]" />
              <Label className="text-white font-medium">Design Prompt</Label>
            </div>
            <div className="relative">
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Describe the website you want... e.g., 'A modern salon website with warm tones, a hero with booking CTA, services showcase, testimonials, and newsletter signup. Target audience is women 25-45 in urban areas.'"
                rows={5}
                className="bg-white/5 border-white/10 text-white placeholder:text-slate-600 rounded-xl resize-none pr-12"
                data-testid="ai-prompt-input"
              />
              <button onClick={() => fileRef.current?.click()} className="absolute bottom-3 right-3 p-2 text-slate-500 hover:text-[#A78BFA] hover:bg-white/5 rounded-lg transition-colors" title="Upload reference images">
                <Image size={18} />
              </button>
              <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleImageUpload(e.target.files)} />
            </div>

            {/* Uploaded Images */}
            {referenceImages.length > 0 && (
              <div className="flex gap-2 mt-3 flex-wrap">
                {referenceImages.map((img, i) => (
                  <div key={i} className="relative group">
                    <img src={img} alt="" className="w-16 h-16 rounded-xl object-cover border border-white/10" />
                    <button onClick={() => setReferenceImages(prev => prev.filter((_, idx) => idx !== i))} className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <X size={8} className="text-white" />
                    </button>
                  </div>
                ))}
                {uploading && <div className="w-16 h-16 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center"><Loader2 size={16} className="animate-spin text-[#8B5CF6]" /></div>}
              </div>
            )}

            {/* Reference URLs */}
            <div className="mt-4">
              <div className="flex items-center gap-2 mb-2">
                <Link2 size={12} className="text-slate-500" />
                <Label className="text-slate-400 text-xs">Reference URLs (optional)</Label>
              </div>
              {referenceUrls.map((url, i) => (
                <div key={i} className="flex gap-2 mb-1.5">
                  <Input value={url} onChange={(e) => { const n = [...referenceUrls]; n[i] = e.target.value; setReferenceUrls(n); }} placeholder="https://example.com" className="bg-white/5 border-white/10 text-white text-xs rounded-lg h-8 flex-1" />
                  {i === referenceUrls.length - 1 ? (
                    <button onClick={() => setReferenceUrls(prev => [...prev, ''])} className="px-2 text-[#A78BFA] text-xs hover:text-white">+ Add</button>
                  ) : (
                    <button onClick={() => setReferenceUrls(prev => prev.filter((_, idx) => idx !== i))} className="px-2 text-slate-500 text-xs hover:text-red-400"><X size={12} /></button>
                  )}
                </div>
              ))}
            </div>
          </GlassCard>

          {/* Advanced Controls */}
          <GlassCard className="overflow-hidden">
            <button onClick={() => setShowAdvanced(!showAdvanced)} className="w-full p-4 flex items-center justify-between text-sm text-slate-300 hover:text-white transition-colors" data-testid="toggle-advanced">
              <span className="flex items-center gap-2"><Target size={14} className="text-[#A78BFA]" /> Advanced Style Controls</span>
              <ChevronDown size={16} className={`transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
            </button>
            {showAdvanced && (
              <div className="p-5 pt-0 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-400 text-xs">Business Type</Label>
                    <Input value={style.business_type} onChange={(e) => setStyle(s => ({ ...s, business_type: e.target.value }))} placeholder="Salon, Store, Portfolio..." className="bg-white/5 border-white/10 text-white rounded-lg mt-1 text-sm h-9" />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-xs">Target Audience</Label>
                    <Input value={style.target_audience} onChange={(e) => setStyle(s => ({ ...s, target_audience: e.target.value }))} placeholder="Women 25-45, local clients..." className="bg-white/5 border-white/10 text-white rounded-lg mt-1 text-sm h-9" />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-xs">Color Palette</Label>
                    <Input value={style.color_palette} onChange={(e) => setStyle(s => ({ ...s, color_palette: e.target.value }))} placeholder="Warm golds, deep purple..." className="bg-white/5 border-white/10 text-white rounded-lg mt-1 text-sm h-9" />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-xs">Layout Style</Label>
                    <select value={style.layout_style} onChange={(e) => setStyle(s => ({ ...s, layout_style: e.target.value }))} className="w-full bg-[#1E1E3A] border border-white/10 text-white rounded-lg px-2 mt-1 text-sm h-9">
                      <option value="modern">Modern & Clean</option>
                      <option value="bold">Bold & Dynamic</option>
                      <option value="minimal">Minimalist</option>
                      <option value="luxury">Luxury & Elegant</option>
                      <option value="playful">Playful & Creative</option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label className="text-slate-400 text-xs">Page Goals</Label>
                  <Input value={style.goals} onChange={(e) => setStyle(s => ({ ...s, goals: e.target.value }))} placeholder="Drive bookings, showcase products, build email list..." className="bg-white/5 border-white/10 text-white rounded-lg mt-1 text-sm h-9" />
                </div>
                <div>
                  <Label className="text-slate-400 text-xs mb-2 block">Sections Wanted</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {sectionOptions.map(s => (
                      <button key={s} onClick={() => setStyle(prev => ({ ...prev, sections_wanted: prev.sections_wanted.includes(s) ? prev.sections_wanted.filter(x => x !== s) : [...prev.sections_wanted, s] }))}
                        className={`px-2.5 py-1 rounded-lg text-[10px] font-medium border transition-all ${style.sections_wanted.includes(s) ? 'bg-[#8B5CF6]/20 text-[#A78BFA] border-[#8B5CF6]/30' : 'bg-white/5 text-slate-500 border-white/5 hover:border-white/10'}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </GlassCard>

          {/* Generate Button */}
          <button
            onClick={handleGenerate}
            disabled={generating || !prompt.trim()}
            className="w-full py-4 rounded-2xl text-base font-semibold bg-gradient-to-r from-[#8B5CF6] via-[#EC4899] to-[#3B82F6] text-white shadow-[0_0_30px_rgba(139,92,246,0.4)] hover:shadow-[0_0_50px_rgba(139,92,246,0.6)] disabled:opacity-50 disabled:shadow-none transition-all flex items-center justify-center gap-3"
            data-testid="generate-btn"
          >
            {generating ? <><Loader2 size={20} className="animate-spin" /> Generating Design...</> : <><Wand2 size={20} /> Generate Design</>}
          </button>

          {/* Result */}
          {result && (
            <GlassCard className="p-6" data-testid="ai-result">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <Sparkles size={16} className="text-[#EC4899]" />
                  <h3 className="font-semibold text-white">Generated Design</h3>
                  <span className="text-xs text-slate-500">{result.blocks?.length} blocks</span>
                </div>
                <div className="flex gap-2">
                  <button onClick={handleGenerate} disabled={generating} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs bg-white/5 border border-white/10 text-slate-300 hover:text-white transition-colors" data-testid="regenerate-btn">
                    <RotateCcw size={12} /> Regenerate
                  </button>
                  <button onClick={handleApply} disabled={applying} className="flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-xs font-medium bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white shadow-[0_0_10px_rgba(139,92,246,0.3)] disabled:opacity-50 transition-all" data-testid="apply-design-btn">
                    {applying ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Apply to {targetPage}
                  </button>
                </div>
              </div>

              {/* Block Preview */}
              <div className="space-y-2">
                {result.blocks?.map((block, i) => {
                  const color = blockTypeColors[block.type] || '#8B5CF6';
                  return (
                    <div key={block.id || i} className="flex items-start gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/5 hover:border-white/10 transition-colors">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-[10px] font-bold text-white" style={{ background: color }}>
                        {i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-xs font-medium px-1.5 py-0.5 rounded" style={{ background: `${color}20`, color }}>{blockTypeLabels[block.type] || block.type}</span>
                        </div>
                        <p className="text-sm text-white font-medium truncate">{block.content?.title || block.content?.quote?.substring(0, 60) || 'Content block'}</p>
                        {block.content?.tagline && <p className="text-xs text-slate-500 truncate mt-0.5">{block.content.tagline}</p>}
                        {block.content?.text && <p className="text-xs text-slate-500 truncate mt-0.5">{block.content.text.substring(0, 100)}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </GlassCard>
          )}
        </div>

        {/* Right: History */}
        <div className="space-y-4">
          <GlassCard className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Layers size={14} className="text-[#A78BFA]" />
              <h3 className="text-sm font-semibold text-white">Design History</h3>
            </div>
            {history.length === 0 ? (
              <p className="text-xs text-slate-500 text-center py-6">No designs generated yet. Enter a prompt and click Generate.</p>
            ) : (
              <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                {history.map(h => (
                  <div key={h.id} className="p-3 rounded-xl bg-white/[0.03] border border-white/5 hover:border-white/10 transition-all group">
                    <p className="text-xs text-white font-medium truncate">{h.prompt?.substring(0, 50)}...</p>
                    <div className="flex items-center gap-2 mt-1.5 text-[10px] text-slate-500">
                      <span>{h.blocks?.length} blocks</span>
                      <span>{h.target_page}</span>
                    </div>
                    <button onClick={() => handleApplyFromHistory(h)} disabled={applying} className="mt-2 w-full py-1.5 rounded-lg text-[10px] font-medium bg-white/5 border border-white/5 text-slate-400 hover:text-white hover:border-[#8B5CF6]/30 opacity-0 group-hover:opacity-100 transition-all">
                      Apply This Design
                    </button>
                  </div>
                ))}
              </div>
            )}
          </GlassCard>

          {/* Quick Tips */}
          <GlassCard className="p-5">
            <h4 className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-3">Tips</h4>
            <div className="space-y-2 text-[11px] text-slate-500">
              <p>Be specific about your business type and goals</p>
              <p>Upload reference images for better results</p>
              <p>Use Advanced Controls to fine-tune the output</p>
              <p>Regenerate to get different layout variations</p>
              <p>Apply designs, then fine-tune in Content Editor</p>
            </div>
          </GlassCard>
        </div>
      </div>
    </div>
  );
}
